'use client';

import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';

/**
 * The single back affordance for the whole app.
 *
 * Every "go back" control renders through this component so the icon, size,
 * colour and hit area stay identical everywhere — before it existed the app
 * shipped three different backs (filled circle + chevron, bare arrow, tinted
 * chevron) on adjacent screens.
 *
 * Shape is a left ARROW (not a chevron), on a transparent circle that fills
 * on hover.
 */
interface BackButtonProps {
  /** Renders a `next/link` when provided, a plain button otherwise. */
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  /** Accessible label; defaults to the localized "Back". */
  label?: string;
  /** Panel/sheet toolbars use the smaller hit area (36px instead of 44px). */
  size?: 'default' | 'compact';
  className?: string;
}

export function BackButton({
  href,
  onClick,
  label,
  size = 'default',
  className,
}: BackButtonProps) {
  const t = useTranslations('common');
  const isCompact = size === 'compact';

  const buttonClass = cn(
    'inline-flex items-center justify-center rounded-full shrink-0 text-foreground',
    'hover:bg-muted active:bg-muted/70 transition-colors',
    isCompact ? 'h-9 w-9' : 'h-11 w-11',
    className
  );

  const icon = (
    <ArrowLeft
      className={cn(
        'rtl:-scale-x-100',
        isCompact ? 'h-5 w-5' : 'h-6 w-6'
      )}
      strokeWidth={2.25}
    />
  );

  const ariaLabel = label ?? t('back');

  if (href) {
    return (
      <Link
        href={href}
        onClick={onClick}
        aria-label={ariaLabel}
        className={buttonClass}
      >
        {icon}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={buttonClass}
    >
      {icon}
    </button>
  );
}
